/**
 * RequestRecordsModal — start a medical-records release request (Phase E.2).
 * The recruiter only chooses how far back to request records (lookback) and the
 * send channel. The PATIENT fills in their own info + the doctor's office(s) on
 * the /sign page (we don't know the office or its fax up front), and the signed
 * release faxes to each office automatically. DOB is an optional prefill — the
 * patient can fill or correct it. POST /contacts/:id/records-request
 * { send: true, channels } sends the /sign link via text, email, or both — SMS
 * goes through the same compliance gate as every other manual send (409 reason /
 * 422 hint). Clones DisqualifyModal's shell.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { Contact } from '../../types';
import { Z } from '../ui';

interface RequestRecordsModalProps {
  contact: Contact;
  onClose: () => void;
  /** Fired after a successful create (sent or minted) so the card refetches. */
  onCreated: (sent: boolean) => void;
  /** Fired when the request was created but the SMS was blocked (409) — the
   * modal STAYS OPEN showing why; the card list still needs a refetch. */
  onBlocked?: () => void;
}

/** Dates-of-service presets (must match crmDocuments.LOOKBACK_PRESETS). */
const LOOKBACK_OPTIONS: { value: string; label: string }[] = [
  { value: '6m', label: 'Last 6 months' },
  { value: '12m', label: 'Last 12 months' },
  { value: '3y', label: 'Last 3 years' },
  { value: '5y', label: 'Last 5 years' },
  { value: 'all', label: 'Entire record' },
];

function blockedReason(reason?: string): string {
  switch (reason) {
    case 'opted_out': return 'contact has opted out of SMS';
    case 'consent_revoked': return 'SMS consent was revoked';
    case 'quiet_hours': return 'outside allowed messaging hours';
    case 'no_phone': return 'contact has no phone number';
    case 'no_email': return 'contact has no email address';
    case 'email_failed': return 'the email could not be delivered';
    default: return reason || 'blocked';
  }
}

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

export default function RequestRecordsModal({ contact, onClose, onCreated, onBlocked }: RequestRecordsModalProps) {
  const canSms = Boolean(contact.phone_e164) && !contact.sms_opt_out;
  const canEmail = Boolean(contact.email);
  const [lookback, setLookback] = useState('5y');
  const [dob, setDob] = useState('');
  // Text and email are independent channels. SMS defaults on when it can
  // actually send (the checkbox is disabled for opted-out/phoneless contacts —
  // a disabled-but-checked box would post send:true straight into the 409 path).
  // When SMS isn't available but email is, email defaults on so the primary
  // action still sends something.
  const [viaSms, setViaSms] = useState(canSms);
  const [viaEmail, setViaEmail] = useState(() => !canSms && canEmail);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set after a 409: the request EXISTS (only the SMS was blocked) — another
  // submit would mint a duplicate request, so the primary action becomes Close.
  const [blocked, setBlocked] = useState(false);

  const willSend = viaSms || viaEmail;
  const canSubmit = !saving && !blocked;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    // Channels reflect the recruiter's picks. When nothing is selected we still
    // pass a non-empty array (the server validates non-empty) but send:false
    // makes the server ignore it and just mint the copyable link.
    const picked = [...(viaSms ? ['sms'] : []), ...(viaEmail ? ['email'] : [])];
    const channels = picked.length ? picked : ['sms'];
    try {
      const res = await api.post(`/api/crm/contacts/${contact.id}/records-request`, {
        lookback,
        patient_dob: dob || undefined,
        send: willSend,
        channels,
      });
      onCreated(Boolean(res.data?.sent));
    } catch (e: any) {
      const d = e?.response?.data || {};
      if (e?.response?.status === 409 && d.reason) {
        // The request exists — keep the modal OPEN so the recruiter sees why
        // it didn't go out; `blocked` retires the submit button.
        setError(`Request created, but it couldn't be sent: ${blockedReason(d.reason)}. You can copy the link from the records card.`);
        setBlocked(true);
        onBlocked?.();
        setSaving(false);
        return;
      }
      const base = d.detail || d.error || e.message || 'Request failed';
      setError(d.hint ? `${base} — ${d.hint}` : base);
      setSaving(false);
    }
  };

  return (
    <div className={`fixed inset-0 ${Z.modalRaised} flex items-start justify-center overflow-y-auto bg-black/50 p-0 sm:p-4`} onClick={onClose}>
      <div
        className="mt-auto w-full max-w-md rounded-t-xl bg-white p-5 shadow-xl sm:mb-auto dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Request medical records"
      >
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Request medical records</h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Sends the patient a release to fill out and e-sign. They enter their doctor’s office(s); the signed
          release faxes to each office automatically.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              How far back to request records
            </label>
            <select value={lookback} onChange={(e) => setLookback(e.target.value)} className={inputCls}>
              {LOOKBACK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
              Patient date of birth <span className="font-normal">(optional — the patient can fill it in)</span>
            </label>
            <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} max={new Date().toISOString().slice(0, 10)} className={inputCls} />
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Send the sign link via</span>
            <div className="flex gap-4">
              <label className="inline-flex min-h-[44px] items-center gap-2 text-sm text-gray-700 dark:text-gray-200 sm:min-h-0">
                <input
                  type="checkbox"
                  checked={viaSms}
                  onChange={(e) => setViaSms(e.target.checked)}
                  disabled={!canSms}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Text message
                {contact.sms_opt_out && <span className="text-xs text-red-500">(opted out)</span>}
                {!contact.phone_e164 && <span className="text-xs text-gray-400">(no phone)</span>}
              </label>
              <label className="inline-flex min-h-[44px] items-center gap-2 text-sm text-gray-700 dark:text-gray-200 sm:min-h-0">
                <input
                  type="checkbox"
                  checked={viaEmail}
                  onChange={(e) => setViaEmail(e.target.checked)}
                  disabled={!canEmail}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Email
                {!contact.email && <span className="text-xs text-gray-400">(no email)</span>}
              </label>
            </div>
          </div>
        </div>

        {error && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 min-h-[44px] sm:min-h-0"
          >
            {blocked ? 'Close' : 'Cancel'}
          </button>
          {!blocked && (
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving
                ? 'Sending…'
                : viaSms && viaEmail
                ? 'Text & email sign link'
                : viaSms
                ? 'Text sign link'
                : viaEmail
                ? 'Email sign link'
                : 'Create request'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
